import api from "../../axiosSetup";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid"; // ✅ Import UUID for unique IDs
import { AppDispatch, RootState } from "../store";
import { fetchReserveUsers, syncUserRoles, updateUser } from "./users-slice";
import { IRole } from "../../../types/IRole";
import { getApiClient } from "@/utils/getApiClient";
import { DEFAULT_NEW_BCR_SIGNATURE_DENSITY, getDefaultBcrSignatureDensity } from "@/utils-3/bcrSignatureSpacing";
import { BcrClassicLogoLayout, BcrSelectableHeaderDesign, DEFAULT_BCR_CLASSIC_LOGO_LAYOUT, DEFAULT_BCR_HEADER_DESIGN, normalizeBcrClassicLogoLayout, normalizeBcrHeaderDesign } from "@/utils-3/bcrHeaderDesign";

export type BcrDisplayOptions = {
  headerDesign: BcrSelectableHeaderDesign;
  classicLogoLayout: BcrClassicLogoLayout;
  showIntendedRecipients: boolean;
  showDepartmentOnPdf: boolean;
  showReserveLogoOnPdf: boolean;
  showResolutionNumbers: boolean;
  showQuorum: boolean;
  defaultBcrPageSize: string;
  defaultBcrContentDensity: string;
  defaultBcrSignatureDensity: string;
};

const defaultBcrDisplayOptions: BcrDisplayOptions = {
  headerDesign: DEFAULT_BCR_HEADER_DESIGN,
  classicLogoLayout: DEFAULT_BCR_CLASSIC_LOGO_LAYOUT,
  showIntendedRecipients: true,
  showDepartmentOnPdf: true,
  showReserveLogoOnPdf: true,
  showResolutionNumbers: true,
  showQuorum: true,
  defaultBcrPageSize: 'LETTER',
  defaultBcrContentDensity: 'normal',
  defaultBcrSignatureDensity: DEFAULT_NEW_BCR_SIGNATURE_DENSITY,
};

interface ReserveListOption {
    _id: string;
    name: string;
    locked: boolean;
    pos?: number;
}

interface ReserveData {
    departments: ReserveListOption[];
    roles: { _id: string; name: string; locked: boolean; hidden: boolean, pos: number }[];
    whereasOptions: ReserveListOption[];
    intendedRecipients: ReserveListOption[];
    bcrDisplayOptions: BcrDisplayOptions;
    quorum: number;
    //Generic
    name: string;
    stringDB: string;
    province: string;
    designatedLand: string;
    reserveImg: string;
    timeZone: string;
}

// Define the Initial State
interface ReserveState {
    loading: boolean;
    error: string | null;
    departments: ReserveListOption[];
    roles: IRole[];
    whereasOptions: ReserveListOption[];
    intendedRecipients: ReserveListOption[];
    bcrDisplayOptions: BcrDisplayOptions;
    quorum: number;
    //Generic
    name: string;
    stringDB: string;
    province: string;
    designatedLand: string;
    reserveImg: string;
    timeZone: string; 
}

const initialState: ReserveState = {
    loading: false,
    error: null,
    departments: [],
    roles: [] as { _id: string; name: string; locked: boolean; hidden: boolean, pos: number }[],
    whereasOptions: [],
    intendedRecipients: [],
    bcrDisplayOptions: defaultBcrDisplayOptions,
    quorum: 3,
    name: 'myReserveName',
    stringDB: 'myReserveDB',
    province: 'myProvince',
    designatedLand: 'myDesignatedLand',
    reserveImg: "",
    timeZone: "",
};

// Create the users slice
const reserveSlice = createSlice({
  name: "reserve",
  initialState,
    reducers: {
        // Department
        addDepartment: (state, action: PayloadAction<string>) => {
            const newDepartment = { _id: uuidv4(), name: action.payload, locked: false };
            state.departments.push(newDepartment);
        },
        removeDepartment: (state, action: PayloadAction<string>) => {
            state.departments = state.departments.filter(dept => dept._id !== action.payload);
        },
        editDepartment: (state, action: PayloadAction<{ _id: string; name: string }>) => {
            state.departments = state.departments.map(dept =>
                dept._id === action.payload._id ? { ...dept, name: action.payload.name } : dept
            );
        },
        // Whereas
        addWhereas: (state, action: PayloadAction<string>) => {
            const newWhereas = { _id: uuidv4(), name: action.payload, locked: false };
            state.whereasOptions.push(newWhereas);
        },
        removeWhereas: (state, action: PayloadAction<string>) => {
            state.whereasOptions = state.whereasOptions.filter(dept => dept._id !== action.payload);
        },
        editWhereas: (state, action: PayloadAction<{ _id: string; name: string }>) => {
            state.whereasOptions = state.whereasOptions.map(dept =>
                dept._id === action.payload._id ? { ...dept, name: action.payload.name } : dept
            );
        },
        // Intended Recipients
        addIntended: (state, action: PayloadAction<string>) => {
            const newIntended = { _id: uuidv4(), name: action.payload, locked: false };
            state.intendedRecipients.push(newIntended);
        },
        removeIntended: (state, action: PayloadAction<string>) => {
            state.intendedRecipients = state.intendedRecipients.filter(dept => dept._id !== action.payload);
        },
        editIntended: (state, action: PayloadAction<{ _id: string; name: string }>) => {
            state.intendedRecipients = state.intendedRecipients.map(dept =>
                dept._id === action.payload._id ? { ...dept, name: action.payload.name } : dept
            );
        },
        // Roles
        addRole: (state, action: PayloadAction<string>) => {
            const newWhereas = { _id: uuidv4(), name: action.payload, locked: false, hidden: false, pos: 32 };
            state.roles.push(newWhereas);
        },
        removeRole: (state, action: PayloadAction<string>) => {
            state.roles = state.roles.filter(dept => dept._id !== action.payload);
        },
        editRole: (state, action: PayloadAction<{ _id: string; name: string }>) => {
            state.roles = state.roles.map(dept =>
                dept._id === action.payload._id ? { ...dept, name: action.payload.name } : dept
            );
        },
    },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReserve.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReserve.fulfilled, (state, action: PayloadAction<ReserveData>) => {
        state.loading = false;
        state.error = null;
        state.departments = action.payload.departments || [];
        state.roles = action.payload.roles || [];
        state.whereasOptions = action.payload.whereasOptions || [];
        state.intendedRecipients = action.payload.intendedRecipients || [];
        state.bcrDisplayOptions = {
          ...defaultBcrDisplayOptions,
          ...(action.payload.bcrDisplayOptions || {}),
          headerDesign: normalizeBcrHeaderDesign(action.payload.bcrDisplayOptions?.headerDesign),
          classicLogoLayout: normalizeBcrClassicLogoLayout(action.payload.bcrDisplayOptions?.classicLogoLayout),
          defaultBcrSignatureDensity: getDefaultBcrSignatureDensity(
            action.payload.bcrDisplayOptions?.defaultBcrSignatureDensity
          ),
        };
        state.quorum = action.payload.quorum || 3;
        //Generic
        state.name = action.payload.name;
        state.reserveImg = action.payload.reserveImg;
        state.stringDB = action.payload.stringDB;
        state.province = action.payload.province;
        state.designatedLand = action.payload.designatedLand;
        state.timeZone = action.payload.timeZone;
      })
      .addCase(fetchReserve.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "An error occurred";
      })
    //Save reserve
        .addCase(updateReserveFields.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(updateReserveFields.fulfilled, (state, action: PayloadAction<any>) => {
          const updated = action.payload;
          state.loading = false;
          // Update only fields that were included in the response
          if (updated.departments) state.departments = updated.departments;
          if (updated.roles) state.roles = updated.roles;
          if (updated.whereasOptions) state.whereasOptions = updated.whereasOptions;
          if (updated.quorum !== undefined) state.quorum = updated.quorum;
          if (updated.name) state.name = updated.name;
          // if (updated.stringDB) state.stringDB = updated.stringDB;
          if (updated.province) state.province = updated.province;
          if (updated.designatedLand) state.designatedLand = updated.designatedLand;
          if (updated.reserveImg) state.reserveImg = updated.reserveImg;
          if (updated.intendedRecipients) state.intendedRecipients = updated.intendedRecipients;
          if (updated.bcrDisplayOptions) {
            state.bcrDisplayOptions = {
              ...state.bcrDisplayOptions,
              ...updated.bcrDisplayOptions,
              headerDesign: normalizeBcrHeaderDesign(
                updated.bcrDisplayOptions.headerDesign ?? state.bcrDisplayOptions.headerDesign
              ),
              classicLogoLayout: normalizeBcrClassicLogoLayout(
                updated.bcrDisplayOptions.classicLogoLayout ?? state.bcrDisplayOptions.classicLogoLayout
              ),
              defaultBcrSignatureDensity: getDefaultBcrSignatureDensity(
                updated.bcrDisplayOptions.defaultBcrSignatureDensity ??
                  state.bcrDisplayOptions.defaultBcrSignatureDensity
              ),
            };
          }
          if (updated.timeZone) state.timeZone = updated.timeZone;
        })
        .addCase(updateReserveFields.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload || "An error occurred while updating the reserve";
        })
        .addCase(appendReserveSuggestion.fulfilled, (state, action) => {
          state.intendedRecipients = action.payload.intendedRecipients || [];
          state.whereasOptions = action.payload.whereasOptions || [];
        })
  },
});

// Async thunk to fetch users from API
export const fetchReserve = createAsyncThunk<ReserveData, string>(
  "reserve/fetchReserve",
  async (reserveId, { rejectWithValue }) => { // ✅ Accept reserveId as the first argument
    try {
      if (typeof reserveId !== 'string') {
        // console.warn("⚠️ fetchReserve called with non-string value:", reserveId);
        return;
      }
      const client = getApiClient();

      const response = await client.get(`/api/reserve/${reserveId}`);
      return response.data.reserve; // ✅ Ensure you're returning `users` from API response
    } catch (error) {
      return rejectWithValue("Failed to fetch users");
    }
  }
);

type ReserveUpdatePayload = {
  reserveId: string;
  updates: Partial<{
    name: string;
    stringDB: string;
    province: string;
    reserveImg: string;
    designatedLand: string;
    departments: ReserveListOption[];
    roles: { _id: string; name: string; locked: boolean }[];
    whereasOptions: ReserveListOption[];
    intendedRecipients: ReserveListOption[];
    bcrDisplayOptions: Partial<BcrDisplayOptions>;
    quorum: number;
    timeZone: string;
  }>;
};

export const appendReserveSuggestion = createAsyncThunk<
  ReserveData,
  { reserveId: string; field: 'intendedRecipients' | 'whereasOptions'; name: string },
  { rejectValue: { message: string } }
>(
  'reserve/appendSuggestion',
  async ({ reserveId, field, name }, { rejectWithValue }) => {
    try {
      const response = await getApiClient().post(`/api/reserve/${reserveId}/suggestions/${field}`, { name });
      return response.data.reserve;
    } catch (error: any) {
      return rejectWithValue({ message: error?.response?.data?.message || 'Failed to add shared suggestion' });
    }
  },
);

// export const updateReserveFields = createAsyncThunk<
//   any, // Can be refined to a Reserve type if needed
//   ReserveUpdatePayload,
//   { rejectValue: string }
// >(
//   "reserve/updateFields",
//   async ({ reserveId, updates }, { dispatch, getState, rejectWithValue }) => {
//     try {
//       const response = await api.patch(`/api/reserve/${reserveId}`, updates);

//       if (updates.roles) {
//         await dispatch(fetchReserveUsers(reserveId));
//       }
      
//       return response.data.reserve;
//     } catch (error: any) {
//       return rejectWithValue(error.response?.data?.message || "Failed to update reserve");
//     }
//   }
// );
export const updateReserveFields = createAsyncThunk<
  any,
  ReserveUpdatePayload,
  { rejectValue: any } // allow structured payload
>(
  'reserve/updateFields',
  async ({ reserveId, updates }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.patch(`/api/reserve/${reserveId}`, updates);

      if (updates.roles) {
        await dispatch(fetchReserveUsers(reserveId));
      }

      return response.data.reserve;
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      // Return entire server payload for 4xx
      if (status && data) {
        return rejectWithValue(data);
      }
      return rejectWithValue({ message: 'Failed to update reserve' });
    }
  }
);

// in a thunks.ts or similar file
export const editRoleAndSyncUsers = createAsyncThunk<
  void,
  { _id: string; name: string },
  { dispatch: AppDispatch; state: RootState }
>("roles/editRoleAndSyncUsers", async ({ _id, name }, { dispatch, getState }) => {
  // Dispatch to update role in reserveReducer
  dispatch(editRole({ _id, name }));

  // Get the updated role from reserveReducer
  const updatedRole = getState().reserveReducer.roles.find(r => r._id === _id);
  if (!updatedRole) return;

  // Dispatch to update users in usersReducer
  dispatch(syncUserRoles({ roleId: _id, updatedRole }));
});

export const deleteRoleAndReassignUsers = createAsyncThunk<
  void,
  string, // roleId to delete
  { dispatch: AppDispatch; state: RootState }
>("roles/deleteRoleAndReassignUsers", async (roleIdToDelete, { dispatch, getState }) => {
  const state = getState();
  const roles = state.reserveReducer.roles;
  const users = state.usersReducer.users;

  // 🔍 Find Department Head role
  const departmentHead = roles.find(role => role.name === "Department Head");
  if (!departmentHead) {
    console.warn("⚠️ Department Head role not found. Cannot reassign users.");
    return;
  }

  // ✅ 1. Remove role from reserveReducer
  dispatch(removeRole(roleIdToDelete));

  // ✅ 2. Reassign users who had the deleted role
  const affectedUsers = users.filter(user => user.roleId === roleIdToDelete);

  affectedUsers.forEach(user => {
    dispatch(
      updateUser({
        userId: user._id,
        user: {
          roleId: departmentHead._id
        }
      })
    );
  });
});

export const { 
    addDepartment,
    removeDepartment,
    editDepartment,
    addWhereas,
    removeWhereas,
    editWhereas,
    addRole,
    editRole,
    removeRole,
    addIntended,
    removeIntended,
    editIntended,
  } = reserveSlice.actions;
export default reserveSlice.reducer;
